import type { CapacidadesPlataforma } from '@/platform';

/**
 * Origem do cliente ("primeiro toque") enviada no cadastro.
 *
 * Quem grava é o site (landing): na primeira visita ele guarda em
 * `localStorage['oa_origem_v1']` de onde a pessoa veio — UTMs, `ref` de
 * afiliado, referer, página de entrada. O app web roda na mesma origem
 * (`/app`), então enxerga a chave e a manda junto no `signup`.
 *
 * No desktop o localStorage do renderer é do perfil do Electron, que nunca
 * passou pela landing: a chave não existe e `lerOrigem` devolve
 * `undefined`. É o esperado — o cadastro segue igual, só sem origem.
 *
 * O conteúdo vem do navegador do visitante, então é tratado como entrada
 * não confiável: só as chaves conhecidas, só strings, tamanho cortado.
 */

export const CHAVE_ORIGEM = 'oa_origem_v1';

/** Tamanho máximo de cada campo. URL de referer longa é cortada, não descartada. */
export const TAMANHO_MAXIMO_CAMPO = 500;

/** Primeiro toque mais antigo que isso já não explica o cadastro de hoje. */
export const VALIDADE_ORIGEM_DIAS = 90;

const MS_POR_DIA = 86_400_000;

export type OrigemCliente = {
  visitanteId?: string;
  /** ISO 8601. */
  primeiraVisitaEm?: string;
  paginaEntrada?: string;
  referer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  ref?: string;
  /** Click ids do Google Ads (web, iOS app→web, iOS web→app). */
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
};

/** Por onde o cadastro foi feito. */
export type ViaCadastro = 'app_web' | 'app_desktop';

/** Campos de texto livre: cortados em `TAMANHO_MAXIMO_CAMPO`. */
const CAMPOS_TEXTO: ReadonlyArray<keyof OrigemCliente> = [
  'visitanteId',
  'primeiraVisitaEm',
  'paginaEntrada',
  'referer',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmContent',
  'utmTerm',
  'ref',
];

/**
 * Click ids têm formato fixo. Fora dele o campo é descartado em vez de
 * cortado: um gclid truncado não casa com conversão nenhuma no Google Ads.
 */
const CAMPOS_CLICK_ID: ReadonlyArray<keyof OrigemCliente> = ['gclid', 'gbraid', 'wbraid'];
const FORMATO_CLICK_ID = /^[A-Za-z0-9_-]{1,200}$/;

/** A `via` sai do alvo da plataforma — a UI nunca adivinha onde está rodando. */
export function viaDoAlvo(alvo: CapacidadesPlataforma['alvo']): ViaCadastro {
  return alvo === 'web' ? 'app_web' : 'app_desktop';
}

/**
 * Filtra um valor qualquer para `OrigemCliente`: só objeto, só as chaves
 * conhecidas, só strings não vazias (cortadas em `TAMANHO_MAXIMO_CAMPO`;
 * click ids só no formato `[A-Za-z0-9_-]{1,200}`). Devolve `undefined` quando nada se aproveita ou quando o primeiro toque
 * passou de `VALIDADE_ORIGEM_DIAS`. `primeiraVisitaEm` que não é data
 * válida é descartado sozinho — os demais campos continuam valendo.
 */
export function sanitizarOrigem(
  bruto: unknown,
  agora: Date = new Date(),
): OrigemCliente | undefined {
  if (!bruto || typeof bruto !== 'object' || Array.isArray(bruto)) return undefined;
  const fonte = bruto as Record<string, unknown>;

  const origem: OrigemCliente = {};
  for (const campo of CAMPOS_TEXTO) {
    const valor = fonte[campo];
    if (typeof valor !== 'string') continue;
    const limpo = valor.trim().slice(0, TAMANHO_MAXIMO_CAMPO);
    if (limpo) origem[campo] = limpo;
  }
  for (const campo of CAMPOS_CLICK_ID) {
    const valor = fonte[campo];
    if (typeof valor !== 'string') continue;
    const limpo = valor.trim();
    if (FORMATO_CLICK_ID.test(limpo)) origem[campo] = limpo;
  }

  if (origem.primeiraVisitaEm !== undefined) {
    const em = Date.parse(origem.primeiraVisitaEm);
    if (Number.isNaN(em)) {
      delete origem.primeiraVisitaEm;
    } else if (agora.getTime() - em > VALIDADE_ORIGEM_DIAS * MS_POR_DIA) {
      return undefined;
    }
  }

  return Object.keys(origem).length > 0 ? origem : undefined;
}

/** Lê a origem gravada pela landing. Qualquer falha vira `undefined`. */
export function lerOrigem(agora: Date = new Date()): OrigemCliente | undefined {
  try {
    const bruto = localStorage.getItem(CHAVE_ORIGEM);
    if (!bruto) return undefined;
    return sanitizarOrigem(JSON.parse(bruto), agora);
  } catch {
    // localStorage bloqueado (aba anônima restrita, iframe) ou JSON quebrado.
    return undefined;
  }
}
